/**
 * Log Sanitization Utilities
 * Prevents sensitive transaction data leakage in error logs
 */

// HIGH FIX: Utility to sanitize sensitive data from logs
export interface SanitizedTransaction {
  id: string;
  type?: string;
  category?: string;
  execution_status?: string;
  // Exclude sensitive fields: amount, planned_amount, actual_amount, description
}

// Sanitize transaction object for safe logging
export const sanitizeTransactionForLog = (tx: any): SanitizedTransaction => {
  // MEDIUM 2 FIX: Add consistent error boundary și input validation
  try {
    if (!tx || typeof tx !== 'object') {
      return { id: 'invalid-transaction' };
    }
    
    // MEDIUM 2 FIX: Add prototype pollution protection pentru consistent security
    if (tx.constructor !== Object && tx.constructor !== undefined) {
      return { id: 'suspicious-object' };
    }
    
    return {
      id: tx.id || 'unknown-id',
      type: tx.type || 'unknown-type',
      category: tx.category || 'unknown-category',
      execution_status: tx.execution_status || 'unknown-status'
    };
  } catch (error) {
    // MEDIUM 2 FIX: Add error boundary pentru consistent error handling
    return { id: 'sanitization-error' };
  }
};

// Sanitize numeric value for safe logging (remove actual amounts)
export const sanitizeNumericValueForLog = (value: any, context?: string): string => {
  // MEDIUM 2 FIX: Add consistent error boundary și input validation
  try {
    if (value === null || value === undefined) {
      return 'null/undefined';
    }
    
    if (typeof value === 'number') {
      if (!isFinite(value)) {
        return 'non-finite-number';
      }
      // Don't log actual amounts - just indicate type
      return 'numeric-value';
    }
    
    return `non-numeric (${typeof value})`;
  } catch (error) {
    // MEDIUM 2 FIX: Add error boundary pentru consistent error handling
    return 'sanitization-error';
  }
};

// Sanitize metrics object for safe logging
export const sanitizeMetricsForLog = (metrics: any, field?: string): string => {
  // MEDIUM 2 FIX: Add consistent error boundary și input validation
  try {
    if (!metrics || typeof metrics !== 'object') {
      return 'invalid-metrics-object';
    }
    
    // MEDIUM 2 FIX: Add prototype pollution protection pentru consistent security
    if (metrics.constructor !== Object && metrics.constructor !== undefined) {
      return 'suspicious-metrics-object';
    }
    
    if (field) {
      // HIGH FIX: Replace dynamic property access with static object destructuring for React Compiler
      // Dynamic property access like metrics[field] blocks optimization
      const {
        plannedAmount, actualAmount, progress, variance,
        plannedCount, completedCount, modifiedCount, unplannedCount,
        skippedCount, totalSkippedAmount, totalUnplannedAmount
      } = metrics;
      
      let value: any;
      switch (field) {
        case 'plannedAmount': value = plannedAmount; break;
        case 'actualAmount': value = actualAmount; break;
        case 'progress': value = progress; break;
        case 'variance': value = variance; break;
        case 'plannedCount': value = plannedCount; break;
        case 'completedCount': value = completedCount; break;
        case 'modifiedCount': value = modifiedCount; break;
        case 'unplannedCount': value = unplannedCount; break;
        case 'skippedCount': value = skippedCount; break;
        case 'totalSkippedAmount': value = totalSkippedAmount; break;
        case 'totalUnplannedAmount': value = totalUnplannedAmount; break;
        default: value = undefined;
      }
      
      if (typeof value === 'number') {
        return `${field}: numeric-value (${isFinite(value) ? 'finite' : 'non-finite'})`;
      }
      return `${field}: ${typeof value}`;
    }
    
    // For general metrics object, just log structure info
    return `metrics-object with ${Object.keys(metrics).length} properties`;
  } catch (error) {
    // MEDIUM 2 FIX: Add error boundary pentru consistent error handling
    return 'sanitization-error';
  }
};

// Sanitize array for safe logging (avoid logging transaction arrays)
export const sanitizeArrayForLog = (arr: any, context?: string): string => {
  // MEDIUM 2 FIX: Add consistent error boundary și input validation
  try {
    if (!Array.isArray(arr)) {
      return `non-array (${typeof arr})`;
    }
    
    // MEDIUM 2 FIX: Add prototype pollution protection pentru consistent security
    if (arr.constructor !== Array && arr.constructor !== undefined) {
      return 'suspicious-array-object';
    }
    
    return `array with ${arr.length} items${context ? ` in ${context}` : ''}`;
  } catch (error) {
    // MEDIUM 2 FIX: Add error boundary pentru consistent error handling
    return 'sanitization-error';
  }
};

// Safe error logging wrapper
export const logSafeError = (context: string, error: Error, additionalInfo?: any): void => {
  const errorInfo = {
    context,
    message: error.message,
    name: error.name,
    // Don't include stack trace with potentially sensitive data
  };
  
  if (additionalInfo) {
    console.error('[SAFE_LOG]', errorInfo, 'Additional info type:', typeof additionalInfo);
  } else {
    console.error('[SAFE_LOG]', errorInfo);
  }
};

// Safe warning logging wrapper
export const logSafeWarning = (context: string, message: string, sensitiveData?: any): void => {
  const warningInfo = {
    context,
    message,
    dataType: sensitiveData ? typeof sensitiveData : 'none'
  };
  
  console.warn('[SAFE_LOG]', warningInfo);
};